import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from 'configs/AppConfig';

const PORTAL_TOKEN = 'portal_auth_token';
const PORTAL_REFRESH = 'portal_refresh_token';

export const portalLogin = createAsyncThunk('portalAuth/login', async ({ email, password }, { rejectWithValue }) => {
  try {
    const { data } = await axios.post(`${API_BASE_URL}/client-auth/login`, { email, password });
    localStorage.setItem(PORTAL_TOKEN, data.token);
    localStorage.setItem(PORTAL_REFRESH, data.refreshToken);
    localStorage.setItem('portal_user', JSON.stringify(data.user));
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Login failed');
  }
});

export const portalRequestMagicLink = createAsyncThunk('portalAuth/magicLink', async ({ email }, { rejectWithValue }) => {
  try {
    const { data } = await axios.post(`${API_BASE_URL}/client-auth/request-magic-link`, { email });
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to send link');
  }
});

export const portalVerifyToken = createAsyncThunk('portalAuth/verify', async ({ token }, { rejectWithValue }) => {
  try {
    const { data } = await axios.get(`${API_BASE_URL}/client-auth/verify/${token}`);
    localStorage.setItem(PORTAL_TOKEN, data.token);
    localStorage.setItem(PORTAL_REFRESH, data.refreshToken);
    localStorage.setItem('portal_user', JSON.stringify(data.user));
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Invalid or expired link');
  }
});

const initialToken = localStorage.getItem(PORTAL_TOKEN);
const initialUser = (() => {
  try { return JSON.parse(localStorage.getItem('portal_user')); } catch { return null; }
})();

const portalAuthSlice = createSlice({
  name: 'portalAuth',
  initialState: {
    loading: false,
    token: initialToken || null,
    user: initialUser,
    message: '',
    showMessage: false,
    magicLinkSent: false,
  },
  reducers: {
    portalSignOut: (state) => {
      state.token = null;
      state.user = null;
      localStorage.removeItem(PORTAL_TOKEN);
      localStorage.removeItem(PORTAL_REFRESH);
      localStorage.removeItem('portal_user');
    },
    portalClearMessage: (state) => {
      state.showMessage = false;
      state.message = '';
      state.magicLinkSent = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(portalLogin.pending, (state) => { state.loading = true; })
      .addCase(portalLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(portalLogin.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(portalRequestMagicLink.pending, (state) => { state.loading = true; })
      .addCase(portalRequestMagicLink.fulfilled, (state) => {
        state.loading = false;
        state.magicLinkSent = true;
      })
      .addCase(portalRequestMagicLink.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      })
      .addCase(portalVerifyToken.pending, (state) => { state.loading = true; })
      .addCase(portalVerifyToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(portalVerifyToken.rejected, (state, action) => {
        state.loading = false;
        state.message = action.payload;
        state.showMessage = true;
      });
  },
});

export const { portalSignOut, portalClearMessage } = portalAuthSlice.actions;
export default portalAuthSlice.reducer;
